import { useState, type ChangeEvent, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { RCta } from '../RCta';
import type { WregScreenEvent } from './wregScreenEvents';
import { SELECT_DETAIL_FIELD, SELECT_LEGAL_DOCUMENT } from '@/consts/events';
import {
  INTAKE_NAME_FIELDS,
  useIntakeErrors,
  type IntakeErrorKey,
} from './wregIntakeErrors';
import { WregIntakeShell } from './WregIntakeShell';
import { LegalFooter, TextRow } from './WregIntakeRows';
import {
  EMPTY_INTAKE,
  intakeNameIsComplete,
  type WregIntakeForm,
  type WregIntakeTextField,
} from './wregIntakeFields';

const TERMS_FIELD = 'terms';

type Props = {
  notice?: string | null;
  initial?: WregIntakeForm;
  onContinue: (patch: Partial<WregIntakeForm>) => void;
  onEvent?: WregScreenEvent;
};

const WregIntake = ({ notice, initial, onContinue, onEvent }: Props) => {
  const { t } = useTranslation();
  const [f, setF] = useState<WregIntakeForm>(initial ?? EMPTY_INTAKE);

  const valid = intakeNameIsComplete(f);
  const { errors, focus, blur, showError, errorId, validateBeforeSubmit } = useIntakeErrors({
    fields: INTAKE_NAME_FIELDS,
    form: f,
    valid,
    step: 'intake',
    t,
    onEvent,
  });

  const set = (k: WregIntakeTextField) => (e: ChangeEvent<HTMLInputElement>) =>
    setF((p) => ({ ...p, [k]: e.target.value }));

  const row = (field: IntakeErrorKey) => ({
    invalid: showError(field),
    errorId: errorId(field),
    error: showError(field) ? errors[field] : undefined,
    onFocus: focus(field),
    onBlur: blur(field),
  });

  const openLegal = (document: 'terms_of_service' | 'privacy_policy') => (e: MouseEvent) => {
    e.stopPropagation();
    onEvent?.(SELECT_LEGAL_DOCUMENT, { document });
  };

  return (
    <WregIntakeShell
      step="intake"
      title={t('wreg.intake.title')}
      sub={t('wreg.intake.sub')}
      notice={notice}
      onEvent={onEvent}
      footer={
        <>
          <RCta
            beforeClick={validateBeforeSubmit}
            dimmed={!valid}
            onClick={() => {
              onEvent?.(SELECT_DETAIL_FIELD, { field: TERMS_FIELD, is_checked: true });
              onContinue({ first: f.first, last: f.last, tcAccepted: true });
            }}
          >
            {t('wreg.intake.next')}
          </RCta>
          <LegalFooter onLegal={openLegal} />
        </>
      }
    >
      <TextRow
        id="wreg-int-first"
        label={t('wreg.intake.first')}
        autoComplete="given-name"
        value={f.first}
        onChange={set('first')}
        {...row('first')}
      />
      <TextRow
        id="wreg-int-last"
        label={t('wreg.intake.last')}
        autoComplete="family-name"
        value={f.last}
        onChange={set('last')}
        {...row('last')}
      />
    </WregIntakeShell>
  );
};

export { WregIntake };
